import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppColors, config } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
import { SectionView } from '../components/SectionView.tsx';
import { SectionHeader } from '../components/SectionHeader';
import BookDemoFormModel from '../components/models/BookDemoFormModel';
import VideoModel from '../components/models/VideoModel';

interface Feature {
  iconPath: string;
  title: string;
  description: string;
}

const featureCards: Feature[] = [
  {
    iconPath: '/static/images/ic_customer_relationship.svg',
    title: '78%',
    description:
      'Over 78% of customers purchase from the first dealership that quickly responds to their inquiry or request.',
  },
  {
    iconPath: '/static/images/ic_attract_customers.svg',
    title: '100X',
    description:
      'Responding to leads within just 5 minutes can increase your chances of conversion by up to 100x.',
  },
  {
    iconPath: '/static/images/ic_sales_down.svg',
    title: '46%',
    description:
      'Over 46% of potential sales are lost simply because of delayed or missed follow-ups with leads.',
  },
  {
    iconPath: '/static/images/ic_purchase_vehicle.svg',
    title: '25%',
    description:
      'Up to 25% of walk-in customers purchase a vehicle during their first visit to the dealership.',
  },
  {
    iconPath: '/static/images/ic_key_handover.svg',
    title: '40%',
    description:
      'Over 40% of walk-in shoppers make a purchase within the same day of their visit.',
  },
  {
    iconPath: '/static/images/ic_car_handover.svg',
    title: '2/3',
    description:
      'Two-thirds of walk-in customers end up purchasing a vehicle within 72 hours of their initial dealership visit.',
  },
];

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  // const [isPlayIntroVideo] = useState<boolean>(false);

  const { toggle, onOpen, onClose } = useToggle();

  const {
    toggle: toggleBookDemoForm,
    onOpen: onOpenBookDemoForm,
    onClose: onCloseBookDemoForm,
  } = useToggle();

  useEffect(() => {
    if (toggle === false) {
      setVideoUrl(undefined);
    }
  }, [toggle]);

  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpen();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCallToAssistant = () => {
    let telLink = `tel:${config.AUTO_ASSISTANT_CONTACT_NUMBER}`;
    window.open(`${telLink}`, '_blank') ||
      window.location.replace(`${telLink}`);
  };

  return (
    <>
      <Page title={'Home'}>
        {/* <Box
          sx={{
            position: 'relative',
            // maxHeight: isPlayIntroVideo ? '100%' : 'auto',
          }}
        >
          <Box
            component='img'
            src='/static/images/ai_gradient_left.svg'
            sx={{
              position: 'absolute',
              left: { xs: -30, md: 0 },
              top: 0,
              height: { xs: '60%', md: 'auto' },
              width: { xs: '60%', md: 'auto' },
            }}
          />

          <Box
            component='img'
            src='/static/images/ai_gradient_right.svg'
            sx={{
              position: 'absolute',
              height: { xs: '60%', md: 'auto' },
              width: { xs: '60%', md: 'auto' },
              right: 0,
              bottom: 0,
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              py: { xs: 2, md: 5 },
              position: 'relative',
            }}
          >
            <Typography
              sx={{
                maxWidth: '70%',
                fontSize: { xs: '1.3rem', md: '2.125rem' },
                fontWeight: 'bold',
                color: AppColors.textBlackColor,
                '& span': {
                  background: AppColors.textGradient,
                  '-webkit-background-clip': 'text', // Clip the background to the text
                  color: 'transparent', // Make the text color transparent to show the gradient
                },
              }}
            >
              The <span>Leads</span> You Never Knew You Had!
            </Typography>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Box
                // ref={videoRef}
                component={'video'}
                autoPlay={false}
                id='intro_video'
                controls
                poster={'/static/home_page/intro_video_thumbnail.png'}
                sx={{
                  maxWidth: '90%',
                  // height: { xs: 'auto', md: '400px' },
                  borderRadius: '16px',
                }}
              >
                <source
                  src={
                    'https://storage-prod.knocard.net/knocard_auto_brand_consultant/Knocard_LandPg.mp4'
                  }
                  type='video/mp4'
                />
              </Box>
            </Box>
          </Box>
        </Box> */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 5,
            px: '10%',
            background: `url('/static/home_page/knocard_auto_banner_small.png')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant='h3' fontWeight={'bold'}>
            The Future of Dealership
            <br />
            Engagement Starts Here
          </Typography>

          <Typography variant='h4' mt={2}>
            See how KnoCard Auto transforms sales with SmartLink technology and
            real-time owner dashboards.
          </Typography>

          <Stack
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-evenly',
              alignItems: 'center',
              width: '100%',
              my: 5,
              gap: 5,
            }}
          >
            <Box
              component={'img'}
              src='/static/home_page/smartlink_overview.png'
              alt='SmartLink Overview'
              sx={{
                height: { xs: '90%', sm: '40%', md: '30%' },
                width: { xs: '90%', sm: '40%', md: '30%' },
              }}
              onClick={() =>
                handlePlayVideo(
                  'https://storage-prod.knocard.net/knocard_auto_brand_consultant/Knocard_LandPg.mp4'
                )
              }
            />

            <Box
              component={'img'}
              src='/static/home_page/owners_dashboard.png'
              alt='SmartLink Overview'
              sx={{
                height: { xs: '90%', sm: '40%', md: '30%' },
                width: { xs: '90%', sm: '40%', md: '30%' },
              }}
              onClick={() =>
                handlePlayVideo(
                  'https://storage-prod.knocard.net/knocard_auto_brand_consultant/Owners_Dashboard.mp4'
                )
              }
            />
          </Stack>

          <Button
            variant='contained'
            sx={{
              background: AppColors.blueColor,
              color: 'white',
              fontSize: 'clamp(0.8rem, 1.2vw, 1.2rem)',
              textAlign: 'center',
              textTransform: 'unset',
              borderRadius: '12px',
              width: 'fit-content',
              px: '10%',
            }}
            onClick={() => {
              onOpenBookDemoForm();
            }}
          >
            Book a Demo
          </Button>
        </Box>
        {/* <Box
          display={'flex'}
          justifyContent={'center'}
          p={5}
          sx={{
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: 3,
              borderRadius: '24px',
              background: `url('/static/home_page/knocard_auto_banner_small.png')`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              color: 'white',
            }}
          >
            <Typography variant='h6'>
              Hi, my name is Miles 👋
              <br />
              I’m the KnoCard Auto Assistant, and I’m here to help!
              <br />
              Got questions? I’ve got answers.
            </Typography>

            <Typography
              variant='h5'
              sx={{
                '& span': {
                  fontWeight: 'bold',
                },
              }}
            >
              📞 Call me anytime: <span>216-400-5617</span>
            </Typography>
          </Box>

          <Box
            component={'img'}
            src='/static/home_page/img_robo_genie.png'
            sx={{
              position: 'absolute',
              top: -10,
            }}
          />
        </Box> */}

        <Box
          display={'flex'}
          justifyContent={'center'}
          p={5}
          sx={
            {
              // width: '100%',
            }
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
              maxWidth: {
                xs: '100%',
                sm: '500px',
                md: '600px',
                lg: '700px',
                xl: '800px',
              },
            }}
          >
            {/* Content Box */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: {
                  xs: 2,
                  sm: 3,
                  md: 3,
                },
                pr: {
                  xs: '20px',
                  sm: '25px',
                  md: '30px',
                  lg: '35px',
                  xl: '40px',
                },
                borderRadius: '24px',
                background: `url('/static/home_page/knocard_auto_banner_small.png')`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                color: 'white',
                width: '100%',
                minHeight: {
                  xs: '80px',
                  sm: '100px',
                  md: '120px',
                  lg: '140px',
                  xl: '160px',
                },
                // minHeight: {
                //   xs: '120px',
                //   sm: '140px',
                //   md: '160px',
                //   lg: '180px',
                //   xl: '200px',
                // },
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                    md: '1.1rem',
                    lg: '1.25rem',
                  },
                  lineHeight: {xs: 1.4, sm: 2},
                  pr: '12%',
                }}
              >
                Hi, my name is Miles 👋
                <br />
                I'm the KnoCard Auto Assistant, and I'm here to help!
                <br />
                Got questions? I've got answers.
              </Typography>

              {/* <Typography
                variant='h5'
                sx={{
                  fontSize: {
                    xs: '1rem',
                    sm: '1.1rem',
                    md: '1.3rem',
                    lg: '1.5rem',
                  },
                  mt: 2,
                  '& span': {
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  },
                }}
                onClick={handleCallToAssistant}
              >
                📞 Call me anytime:{' '}
                <span>{config.AUTO_ASSISTANT_CONTACT_NUMBER}</span>
              </Typography> */}
            </Box>

            {/* Robot Image - Positioned absolutely within the flex container */}
            <Box
              component={'img'}
              // src='/static/home_page/img_robo_genie.png'
              src='/static/images/img_ai_robot.png'
              sx={{
                position: 'absolute',
                right: {
                  xs: '-50px',
                  sm: '-60px',
                  md: '-60px',
                  lg: '-80px',
                  xl: '-100px',
                },
                top: '50%',
                transform: 'translateY(-50%)',
                height: {
                  xs: '140px',
                  sm: '180px',
                  md: '220px',
                  lg: '260px',
                  xl: '300px',
                },
                width: 'auto',
                zIndex: 2,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: AppColors.aiGradientCross,
            // p: 4,
            py: 4,
            px: '10%',
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              background: AppColors.whiteTransparent,
              borderRadius: '20px',
              px: 2,
              py: 1,
            }}
          >
            Facts that speak for themselves
          </Typography>

          <Typography
            variant='h4'
            sx={{
              my: 2,
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            We Just Follow The Data
          </Typography>

          <Typography
            variant='subtitle2'
            sx={{ color: 'white', opacity: '80%' }}
          >
            Walk-In Customers: High Purchase Intent
          </Typography>

          <Grid container gap={2} mt={1} justifyContent={'center'}>
            {featureCards.map((card) => {
              return (
                <Grid
                  size={{ xs: 12, sm: 5.5, md: 3.5 }}
                  key={card.title}
                  display={'flex'}
                >
                  <Paper
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: '16px',
                      height: 'auto', // Make the Paper fill the Grid item
                      display: 'flex', // Ensure vertical alignment inside Paper
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box
                      component={'img'}
                      src={card.iconPath}
                      alt='Icon'
                      height={'100px'}
                      width={'auto'}
                    />
                    <Typography
                      variant='h5'
                      sx={{
                        color: AppColors.textBlackColor,
                        fontWeight: 600,
                        py: 1,
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: AppColors.grayThreeColor,
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* <Box
            component={'img'}
            src='/static/images/img_ai_robot.png'
            sx={{
              display: { xs: 'none', md: 'flex' },
              height: '200px',
              position: 'absolute',
              top: '10%',
              right: '10%',
            }}
          /> */}
        </Box>
        <Grid
          container
          sx={{
            mx: '10%',
            my: 4,
            p: { xs: 1, md: 0 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            background: AppColors.aiGradientTransparent,
            borderRadius: '16px',
            gap: 4,
          }}
        >
          <Grid size={{ xs: 12, sm: 3.5, md: 3.5 }}>
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Box
                component={'img'}
                src='/static/images/img_enterprise_dashboard.png'
                sx={{
                  width: { xs: '100%', md: '115%' }, // Slightly larger to compensate for crop
                  transform: { xs: '', md: 'translateX(-15%)' },
                }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 7.5, md: 8 }}>
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <SectionHeader text={'Powered by KnoCard Smartlink'} />

              <Typography
                sx={{
                  fontSize: {
                    xs: 'clamp(1.75rem, 4vw, 3rem)',
                    md: 'clamp(1.25rem, 3vw, 2.25rem)',
                  },
                  fontFamily: 'gloock',
                  textAlign: 'center',
                }}
              >
                The New Road to Revenue
              </Typography>
            </Stack>
          </Grid>

          {/* <Grid size={{ xs: 12, sm: 3.5, md: 3.5 }} pr={{ xs: 0, md: 2 }}>
            <Box
              onClick={() => {
                onOpen();
                // setIsPlayIntroVideo(true);
              }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                component='img'
                src='/static/images/img_banner.png'
                alt='video thumbnail'
                sx={{ width: { xs: '100%', md: '100%' } }}
              />

              <Box
                component='img'
                src='/static/images/ic_play_circle.svg'
                alt='Play'
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  cursor: 'pointer',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '25px', md: '50px' },
                  height: { xs: '25px', md: '50px' },
                }}
              />
            </Box>
          </Grid> */}
        </Grid>
        <SectionView
          idx={0}
          label='Start The Process'
          title='Send Smartlink'
          subtitle=''
          features={[
            '<span>Lead info</span> is entered — a trackable SmartLink is created.',
            '<span>Customer receives a branded link</span> with rep contact, vehicle, and dealer info.',
            '<span>All activity</span> updates the sales rep’s pipeline and owner’s dashboard in real time.',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/home_page/img_mobile_step_1.png'
              : '/static/home_page/img_1_smartLink_sent.png'
          }
        />
        <SectionView
          idx={1}
          label='Test Drive'
          title='Test Drive'
          subtitle=''
          features={[
            'Customers who test drive are <span>3x more likely</span> to submit a credit application the same day compared to those who don’t.',
            '<span>62%</span> of customers who test drive a vehicle are <span>likely to move forward</span> with a purchase decision.',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/home_page/img_mobile_step_2.png'
              : '/static/home_page/img_2_test_drive.png'
          }
        />
        <SectionView
          idx={2}
          label='Finance App'
          title='Finance App'
          subtitle=''
          features={[
            '<span>90%</span> of customers who complete a credit application at the dealership purchase a vehicle within <span>48–72 hours.</span>',
            'Customers who <span>fill out a credit app</span> in-store vs. online are <span>40% more likely to close the deal the same day.</span>',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/home_page/img_mobile_step_3.png'
              : '/static/home_page/img_3_finance_application.png'
          }
        />
        <SectionView
          idx={3}
          label='Sold Vehicle'
          title='Sold Vehicle'
          subtitle=''
          features={[
            '<span>75% of repeat buyers</span> say their decision to return was based on the relationship with their salesperson or dealership staff.',
            'It costs <span>5–10x more</span> to acquire a new customer than to sell again to a previous buyer.',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/home_page/img_mobile_step_4.png'
              : '/static/home_page/img_4_sold_vehicle.png'
          }
        />
        <SectionView
          idx={4}
          label='Google Review'
          title='Google Review'
          subtitle=''
          features={[
            'Customers who leave with a positive review and a follow-up contact are <span>3x more likely</span> to return within 3 years.',
            '<span>86%</span> of car buyers say they won’t consider a dealership with less than a 4-star rating on Google.',
            'A dealership with <span>more positive</span> Google reviews will appear higher in Google Maps and Local Pack search results, <span>leading to more inbound calls and walk-ins.</span>',
          ]}
          footerText=''
          imagePath={
            isMobile
              ? '/static/home_page/img_mobile_step_5.png'
              : '/static/home_page/img_5_google_review.png'
          }
        />
        <Box
          sx={{
            background: AppColors.aiGradientCross,
            borderRadius: isMobile ? 0 : '20px',
            mx: isMobile ? 0 : '10%',
            // position: 'relative',
            mb: isMobile ? 0 : 8,
          }}
        >
          <Grid
            container
            display={'flex'}
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            p={4}
            sx={{
              background: `url('/static/images/img_waves_background.svg')`,
              backgroundPosition: `${isMobile ? 'top' : 'center'}`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: isMobile ? 'auto' : 'auto 100%',
            }}
          >
            <Grid
              size={{ xs: 12, md: 4.5, lg: 3.5 }}
              sx={{ textAlign: { xs: 'center', md: 'start' } }}
            >
              <Typography
                component='p'
                sx={{
                  fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontWeight: 600,
                  color: AppColors.whiteColor,

                  my: 1,
                }}
              >
                It’s All Just Math.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  color: AppColors.whiteColor,
                  my: 1,
                }}
              >
                {[
                  'The more leads you capture,',
                  'the more test drives you book.',
                  'The more test drives you book,',
                  'the more finance apps you collect.',
                  'The more apps you collect,',
                  'the more cars you sell.',
                  'The more cars you sell,',
                  'the more reviews you earn.',
                  'The more reviews you earn,',
                  'the more new customers walk through the door.',
                ].map((item) => {
                  return <Typography component={'span'}>{item}</Typography>;
                })}
              </Box>

              <Typography
                sx={{
                  pb: 1,
                  color: AppColors.whiteColor,
                  fontWeight: 600,
                  fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                }}
              >
                You don’t have to write the formula, you just have to follow it.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4.5, lg: 3.5 }}>
              <Paper
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Avatar
                  // src='/static/images/img_rob_profile_pic.jpg'
                  src='/static/images/rob_pickering.png'
                  sx={{
                    width: '160px',
                    height: '160px',
                  }}
                />
                <Typography
                  sx={{
                    py: 2,
                    fontStyle: 'italic',
                  }}
                >
                  “I believe what we’re bringing to dealerships is one of the
                  most powerful sales tools in the industry — and within 90
                  days, you’ll wonder how you ever did business without
                  KnoCard.”
                </Typography>
                <Typography
                  sx={{
                    fontStyle: 'italic',
                    fontWeight: 600,
                    background: AppColors.textGradient,
                    '-webkit-background-clip': 'text',
                    color: 'transparent',
                  }}
                >
                  — Robert Pickering, Founder & CEO
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}

      <BookDemoFormModel
        onClose={onCloseBookDemoForm}
        toggle={toggleBookDemoForm}
        onSubmitSuccess={() => {}}
      />
    </>
  );
};

export default Home;
